import { ReactNode, useEffect, useRef, useState } from 'react';
import { DataPackageV2Component } from '@vivli/shared/features/data-package/components';
import { DataPackageStatusEnum, DataPackageValidationModeEnum } from '@vivli/features/studies/infrastructure/enum';
import { IDataPackage } from '@vivli/shared/features/data-package/infrastructure/interface';
import { useStudyContext } from '@vivli/features/studies/infrastructure/context';
import { IHistory } from '@vivli/shared/infrastructure/interface';
import {
    useActiveUserContext,
    useDataPackageApiContext,
    useDataRequestsApiContext,
    useModalApiContext,
    useOrganizationsApiContext,
    useStudiesApiContext,
    useToastApiContext,
    useUtilityApiContext
} from '@vivli/core/infrastructure/context';
import { finalize, first, switchMap } from 'rxjs/operators';
import { StudyRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { IStudy } from '@vivli/features/studies/infrastructure/interface';

import { useTranslation } from 'react-i18next';
import { LocalStorageKeyEnum, TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { useExternalLinkPermissionsHook, useStudy } from '@vivli/features/studies/infrastructure/hook';
import { IListingRequest } from '@vivli/features/listing-request/infrastructure/interface';
import { IOrganization } from '@vivli/features/organizations/infrastructure/interface';
import { StudyExternalLinksComponent } from '@vivli/shared/components';
import { Subject } from 'rxjs';
import { useDataPackageFileTypes } from '@vivli/shared/features/data-package/infrastructure/hook';
import { useErrorHandling, useStorage } from '@vivli/shared/infrastructure/hook';

interface StudyDataPackageViewFeatureProps {
    dataPackageId: string;
    ipdContentType: 'Full' | 'Basic';
    studyId: string;
    requestedStudyId?: string;
    dataRequestId?: string;
    onFileRemoved?: () => void;
    onFileAdding?: () => void;
    onValidation?: () => void;
    onPackageSubmitted?: (dataPackage: IDataPackage) => void;
    onReady?: () => void;
    emailOnSuccess?: any; //currently tested/supported only for listing request
    onPackageAvailable?: (dataPackage: IDataPackage) => void;
    thisRequestOnlyIpd?: boolean;
    showExternalLinks?: boolean;
    title?: string;
    orgErrorText?: string;
    rowHeight?: number;
}

export const StudyIpdDataPackageFeature = ({
    dataPackageId,
    ipdContentType,
    studyId,
    requestedStudyId,
    dataRequestId,
    onFileAdding,
    onFileRemoved,
    onPackageSubmitted,
    onValidation,
    onReady,
    emailOnSuccess,
    onPackageAvailable,
    thisRequestOnlyIpd,
    showExternalLinks = true,
    title,
    orgErrorText,
    rowHeight
}: StudyDataPackageViewFeatureProps) => {
    const [isSubmittingPackage, setIsSubmittingPackage] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [organization, setOrganization] = useState<IOrganization>(null);
    const { handleError } = useErrorHandling();

    const { getItem } = useStorage();
    const dataPackageService = useDataPackageApiContext();
    const dataRequestsService = useDataRequestsApiContext();
    const modalService = useModalApiContext();
    const { user: currentUser } = useActiveUserContext();
    const studiesService = useStudiesApiContext();
    const toastService = useToastApiContext();
    const orgsService = useOrganizationsApiContext();

    const { sendEmailForListingRequest } = useUtilityApiContext();
    const { study, setStudy } = useStudy(studyId);
    const { canEditLinks, isLoadingPermissions } = useExternalLinkPermissionsHook(study);
    const { t } = useTranslation();
    const dataPackageFileTypes = useDataPackageFileTypes();

    const submitStudyRef = useRef<Subject<IDataPackage>>(new Subject<IDataPackage>());
    const submittedStudyRef = useRef<Subject<IDataPackage>>(new Subject<IDataPackage>());
    const rowHeightToUSe = rowHeight ?? 70;

    const _handleModalError = (error: string) => {
        handleError(`${error}. Please try again or contact Vivli Support at support@vivli.org.`);
    };

    const addHistoryEntryToStudy = (dataPackage: IDataPackage, study: IStudy) => {
        const dataPackageName = dataPackage.title;
        const history: IHistory = {
            userId: currentUser.userId,
            dateTime: new Date(),
            comment: `Ipd Package uploaded was ${dataPackageName}`,
            description: 'Ipd Package Uploaded',
            userEmail: currentUser.email,
            userName: currentUser.name
        };

        const newHistoryEntries = study.studyHistoryEntries ? [...study.studyHistoryEntries] : [];

        const updatedStudy = {
            ...study,
            studyHistoryEntries: [...newHistoryEntries, history]
        };

        studiesService
            .updateWithNewHistoryEntry(updatedStudy)
            .pipe(first())
            .subscribe(
                () => {
                    if (requestedStudyId) {
                        handleRequestedStudyStatus(dataPackage);
                    } else {
                        handleIpdSubmittedSuccessfully(dataPackage);
                    }
                },
                (e) => {
                    toastService.error(
                        `An error occurred adding Ipd history.  Please contact Vivli support for more information. Message: ${e}`
                    );
                }
            );
    };

    const handleIpdUploadEmailNeeded = (listingRequest: IListingRequest) => {
        sendEmailForListingRequest(listingRequest, 'StudyIpdUploaded').pipe(first()).subscribe({
            error: _handleModalError
        });
    };

    const handleIpdSubmittedSuccessfully = (dataPackage: IDataPackage) => {
        onPackageSubmitted && onPackageSubmitted(dataPackage);

        if (emailOnSuccess) {
            handleIpdUploadEmailNeeded(emailOnSuccess);
        }

        if (
            dataPackage.dataPackageType === 'StudyIPD' &&
            ((study.status === 'Posted' && study.studyIPDDataPackageDoi === null) || 'DOINotCreated')
        ) {
            dataPackageService
                .createDoi(dataPackageId)
                .pipe(
                    first(),
                    finalize(() => {
                        setTimeout(() => window.location.reload(), 2000); //temporary fix to 13201
                    })
                )
                .subscribe(
                    () => {
                        modalService.message(
                            'Thank you for uploading the data for this study. It will now be available for further analysis.'
                        );
                    },
                    (error) => {
                        _handleModalError(`The data package is submitted, but the DOI for the package is not created. Error: ${error}.`);
                    }
                );
        } else {
            modalService.message('Thank you for uploading the data for this study. It will now be available for further analysis.');
            setTimeout(() => window.location.reload(), 2000); //temporary fix to 13201
        }
    };

    const handleRequestedStudyStatus = (dataPackage: IDataPackage) => {
        // this case needs to be called for a data request, requested study ONLY
        dataRequestsService
            .setDataRequestStudyStatus(dataRequestId, requestedStudyId, StudyRequestStatusEnum.IPDProvided, '')
            .pipe(first())
            .subscribe(() => {
                handleIpdSubmittedSuccessfully(dataPackage);
            }, modalService.error);
    };

    const handleStudyIpdStatus = (dataPackage: IDataPackage) => {
        if (study !== undefined && study) {
            let submittedStatusToSet = thisRequestOnlyIpd ? false : true;
            if (!submittedStatusToSet && study.hasSubmittedIpd) {
                //if study already has template Ipd, leave status true
                //even in the case of a thisRequestOnlyIpd upload
                submittedStatusToSet = true;
            }
            studiesService
                .setStudyIpdSubmittedStatus(study, submittedStatusToSet)
                .pipe(first())
                .subscribe((updatedStudy) => {
                    setStudy(updatedStudy);

                    addHistoryEntryToStudy(dataPackage, updatedStudy);
                });
        } else {
            // this is for unlisted study data package creation
            if (requestedStudyId) {
                handleRequestedStudyStatus(dataPackage);
            }
        }
    };

    const submitDataPackage = (dataPackage: IDataPackage) => {
        setIsSubmittingPackage(true);
        dataPackageService
            .getDataPackage(dataPackage.id)
            .pipe(
                first(),
                switchMap((dp: IDataPackage) => {
                    return dataPackageService.updateDataPackage({
                        ...dataPackage,
                        ...dp,
                        dataPackageFileDescriptors: dataPackage.dataPackageFileDescriptors,
                        status: DataPackageStatusEnum.Submitted
                    });
                }),
                // Clear the in-flight flag however this settles. isSubmittingPackage is passed down as
                // isSubmittingFiles and is the first term of the Submit button's disabled expression, so
                // a rejected submit used to leave the button permanently dead — the server's "did not
                // upload properly" rejection showed its modal, handleSubmitError returned, and nothing
                // ever reset the flag. Only a remount (i.e. reloading the page) brought Submit back,
                // which is why the rejection message tells the user to close the browser. (DEV-746)
                //
                // finalize covers completion, error and unsubscribe, so handleSubmitError needs no
                // reset of its own. Resetting on success is harmless: the package is Submitted by then,
                // isDraft is false, and the button is not rendered at all.
                finalize(() => setIsSubmittingPackage(false))
            )
            .subscribe({
                next: (updatedPackage) => {
                    handleStudyIpdStatus(updatedPackage);

                    submitStudyRef.current.next(updatedPackage);
                },
                error: handleSubmitError
            });

        return submitStudyRef.current;
    };

    const handlePackageSubmitted = (dataPackage: IDataPackage) => {
        //is there a cookie to prevent this message from showing?
        const showConfirmation = getItem(LocalStorageKeyEnum.ShowIpdSubmitConfirmation) !== false;
        const additionalMessage = showAdditionalMessage(dataPackage);
        if (showConfirmation) {
            modalService.confirm(
                <div style={{ textAlign: 'left' }}>
                    <div>Are you sure all files have been uploaded and assigned file types? This action cannot be undone.</div>
                    <p />
                    <div>{additionalMessage}</div>
                    <p />
                    <div>Click Yes to confirm this, or No to modify any of those selections.</div>
                </div>,
                {
                    onConfirm: () => {
                        // pass along the result to the next subject
                        submitDataPackage(dataPackage).subscribe({
                            next: (dp) => {
                                submittedStudyRef.current.next(dp);
                            }
                        });
                    },
                    neverShowAgainCookieName: LocalStorageKeyEnum.ShowIpdSubmitConfirmation
                }
            );
        } else {
            submittedStudyRef.current = submitDataPackage(dataPackage);
        }

        return submittedStudyRef.current;
    };

    const showAdditionalMessage = (dataPackage: IDataPackage): string => {
        let show = true;
        //is there a default for organization?
        if (organization !== undefined && organization) {
            show = !organization.publicAccessByDefaultFileTypes || organization.publicAccessByDefaultFileTypes.length == 0;
        } else {
            show = false;
        }
        let message = '';
        if (show) {
            //form the list of PA file types
            const paFileTypes = dataPackage.dataPackageFileDescriptors.filter((x) => x.publicAccessGranted).map((x) => x.type);
            if (paFileTypes.length > 0) {
                message = `${paFileTypes.join(', ')}`;
            } else {
                show = false; //no PA files
            }
        }

        if (show) {
            return `You also have specified that the following file types should be made available on the Study Documents tab, to logged-in users who have not yet submitted a data request: ${message}.`;
        }

        return '';
    };

    const handleSubmitError = (error: ReactNode) => {
        dataPackageService.handleSubmitErrorWithFileCheck(error, true, `${studyAmrTranslation()} ${study.sponsorProtocolId}`);
    };

    const toCapitalize = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    const studyAmrTranslation = () => {
        return toCapitalize(t(TranslationKeyEnum.study));
    };

    const handlePubliclyAvailableOrg = (publiclyAvailableOrgId: string) => {
        if (publiclyAvailableOrgId && publiclyAvailableOrgId !== 'FakeOrgId') {
            orgsService
                .getOrganization(publiclyAvailableOrgId)
                .pipe(first())
                .subscribe({
                    next: setOrganization,
                    error: (error) => {
                        if (error.toString().includes('Unauthorized') && orgErrorText) {
                            handleError(`Error getting organization. ${orgErrorText}`, '');
                        } else {
                            _handleModalError(`Error getting organization: ${error?.Message || error?.message || error}`);
                        }
                        setIsDisabled(true);
                    }
                });
        }
    };

    useEffect(() => {
        handlePubliclyAvailableOrg(thisRequestOnlyIpd ? null : study?.orgId);
    }, [study, thisRequestOnlyIpd]);

    useEffect(() => {
        setIsDisabled(!dataPackageFileTypes.requiredTypes);
    }, [dataPackageFileTypes]);

    return (
        <div>
            {isDisabled && orgErrorText && (
                <div style={{ width: '60%' }}>
                    <strong>{orgErrorText}</strong>
                </div>
            )}
            {!isDisabled && (
                <div style={{ marginBottom: 20 }}>
                    <DataPackageV2Component
                        dataPackageId={dataPackageId}
                        readOnly={false}
                        allowDownload={!!dataPackageId}
                        allowUpload={true}
                        allowSubmit={true}
                        allowDelete={true}
                        showFileType={true}
                        showPubliclyAvailable={!thisRequestOnlyIpd}
                        validationMode={DataPackageValidationModeEnum.All}
                        onFileAdded={onFileAdding}
                        onFileRemoved={onFileRemoved}
                        onPackageSubmit={handlePackageSubmitted}
                        onValidation={onValidation}
                        isSubmittingFiles={isSubmittingPackage}
                        onReady={onReady}
                        publicAccessByDefaultFileTypes={organization?.publicAccessByDefaultFileTypes}
                        rowHeight={rowHeightToUSe}
                        title={title}
                        {...dataPackageFileTypes}
                    />
                </div>
            )}
            {!isDisabled && showExternalLinks && study && !isLoadingPermissions && (
                <StudyExternalLinksComponent
                    externalLinks={study.externalLinks}
                    documentId={study.id}
                    editable={canEditLinks}
                    onLinksUpdated={setStudy}
                />
            )}
        </div>
    );
};
